/**
 * LibrarySectionTabs — Phase 152.AA.
 *
 * Sticky segmented control rendered under the page title on the
 * Library tab. Mirrors the visual language of the generator's
 * Simplified/Full/Skill-Focused control so teachers recognise it.
 *
 * Sections:
 *   - lessons   : Lessons & Units (default)
 *   - summative : Summative
 *   - support   : Classroom Support
 *   - isps      : Individual Student Plans
 *
 * Counts shown next to each label when > 0. URL state is owned by
 * the parent (Dashboard.jsx) — this component only renders + emits.
 */
import { BookOpen, Brain, ClipboardCheck, ClipboardList, User2 } from "lucide-react";

const SECTIONS = [
  { key: "lessons",   label: "Lessons & Units",          Icon: BookOpen },
  { key: "summative", label: "Summative",                Icon: ClipboardCheck },
  { key: "support",   label: "Classroom Support",        Icon: Brain },
  { key: "isps",      label: "Individual Student Plans", Icon: User2 },
  { key: "supply",    label: "Supply Plans",             Icon: ClipboardList },
];

export default function LibrarySectionTabs({ section, counts = {}, onChange }) {
  return (
    <div
      className="sticky top-0 z-20 bg-[#f9fafb]/95 backdrop-blur supports-[backdrop-filter]:bg-[#f9fafb]/85 -mx-1 px-1 pt-2 pb-3"
      data-testid="library-section-tabs"
    >
      <div className="ae-card overflow-hidden">
        <nav
          className="grid grid-cols-2 sm:grid-cols-5 divide-x divide-[#4a6984]/10"
          role="tablist"
          aria-label="Library sections"
        >
          {SECTIONS.map(({ key, label, Icon }) => {
            const active = section === key;
            const count = counts[key];
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={`library-section-${key}`}
                data-active={active ? "true" : "false"}
                onClick={() => onChange(key)}
                className={[
                  "flex items-center justify-center gap-2 px-3 py-3.5 text-xs sm:text-sm font-extrabold uppercase tracking-[0.06em] transition relative",
                  active
                    ? "text-[#1c2d3d] bg-white"
                    : "text-[#4a6984]/70 hover:text-[#1c2d3d] hover:bg-white/50",
                ].join(" ")}
              >
                <Icon
                  className={[
                    "h-4 w-4 flex-shrink-0",
                    active ? "text-[#2a7d6b]" : "text-[#4a6984]/60",
                  ].join(" ")}
                />
                <span className="truncate">{label}</span>
                {typeof count === "number" && count > 0 && (
                  <span
                    data-testid={`library-section-${key}-count`}
                    className={[
                      "ml-1 inline-flex items-center justify-center min-w-[20px] px-1.5 py-0.5 rounded-full text-[10px] font-bold",
                      active
                        ? "bg-[#96ded1]/40 text-[#1c2d3d]"
                        : "bg-[#4a6984]/10 text-[#4a6984]/80",
                    ].join(" ")}
                  >
                    {count}
                  </span>
                )}
                {active && (
                  <span
                    aria-hidden="true"
                    className="absolute bottom-0 left-3 right-3 h-0.5 bg-[#2a7d6b] rounded-t"
                  />
                )}
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
